/*
 * df-perf.js — Performance enhancements.
 *
 * 1. Native lazy loading polyfill via IntersectionObserver
 * 2. Auto-decode images with decoding="async"
 * 3. Connection-aware: pausa autoplay de videos en redes lentas
 * 4. Adaptive image quality basado en network speed
 * 5. Above-fold image preload hint
 * 6. Lazy-init <video> (data-src → src) cuando entra en viewport
 * 7. Entry animations bridge: aplica `.is-in` cuando aparecen los .df-anim-*
 * 8. Idle prefetch de páginas frecuentes (después de 3s sin interacción)
 * 9. Preconnect a CDN crítico al primer hover sobre links
 */
(function () {
  'use strict';

  // ===========================================================
  // 1. Network awareness — adapta calidad según connection
  // ===========================================================
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var isSlowNet = false;
  var isSaveData = false;

  function checkNetwork() {
    if (!connection) return;
    var t = connection.effectiveType || '';
    isSlowNet = (t === 'slow-2g' || t === '2g' || t === '3g');
    isSaveData = !!connection.saveData;
    document.documentElement.setAttribute('data-df-net', t || 'unknown');
    if (isSaveData) document.documentElement.setAttribute('data-df-savedata', '1');
  }
  checkNetwork();
  if (connection && connection.addEventListener) {
    connection.addEventListener('change', checkNetwork);
  }

  // ===========================================================
  // 2. Pausa videos autoplay en red lenta o save-data
  // ===========================================================
  if (isSlowNet || isSaveData) {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('video[autoplay], video[data-df-autoplay]').forEach(function (v) {
        try { v.pause(); v.removeAttribute('autoplay'); } catch (e) {}
      });
    });
  }

  // ===========================================================
  // 3. Lazy-load <video data-src> con IntersectionObserver
  //    Solo cuando están a punto de entrar en viewport
  // ===========================================================
  if ('IntersectionObserver' in window) {
    var videoObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          // Pausar al salir
          if (entry.target.tagName === 'VIDEO' && !entry.target.paused) {
            try { entry.target.pause(); } catch (e) {}
          }
          return;
        }
        var v = entry.target;
        if (v.dataset.src && !v.src) {
          v.src = v.dataset.src;
          v.removeAttribute('data-src');
        }
        if (!isSlowNet && !isSaveData && v.hasAttribute('data-df-autoplay-on-view')) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.1 });

    document.addEventListener('DOMContentLoaded', function () {
      // Los reels (df-home-reels) llevan su propio observer con límite de dos
      // vídeos a la vez. Observarlos también desde aquí los pausaba mientras
      // su play() aún no había resuelto: cinco AbortError por carga.
      document.querySelectorAll('video[data-src]:not(.df-hreels__video), video[data-df-lazy-video]:not(.df-hreels__video)').forEach(function (v) {
        videoObserver.observe(v);
      });
    });
  }

  // ===========================================================
  // 4. Entry animations: aplica .is-in a .df-anim-* al entrar
  // ===========================================================
  if ('IntersectionObserver' in window) {
    var animObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          animObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    function observeAnims(root) {
      (root || document).querySelectorAll('[class*="df-anim-"]:not(.is-in)').forEach(function (el) {
        animObserver.observe(el);
      });
    }
    document.addEventListener('DOMContentLoaded', function () { observeAnims(document); });
    // Re-observa cuando JS añade contenido dinámico (load-more, recently-viewed)
    window.dfObserveAnims = observeAnims;
  }

  // ===========================================================
  // 5. Add decoding="async" to all images that don't have it
  // ===========================================================
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('img:not([decoding])').forEach(function (img) {
      img.decoding = 'async';
    });
  });

  // ===========================================================
  // 6. Lazy load — fallback para images sin loading="lazy"
  //    (native lazy es ampliamente soportado, solo polyfill para edge)
  // ===========================================================
  if (!('loading' in HTMLImageElement.prototype) && 'IntersectionObserver' in window) {
    var imgObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var img = entry.target;
        if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
        if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
        imgObserver.unobserve(img);
      });
    }, { rootMargin: '200px 0px' });
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        // Si native lazy no funciona, convertimos a data-src
        if (img.src && !img.complete) {
          img.dataset.src = img.src;
          img.removeAttribute('src');
          imgObserver.observe(img);
        }
      });
    });
  }

  // ===========================================================
  // 7. Idle prefetch: tras 3s sin interacción, prefetch páginas
  //    típicas de navegación (collections/all, cart, novedades)
  // ===========================================================
  if ('requestIdleCallback' in window && window.fetch) {
    var prefetchedIdle = false;
    function prefetchIdle() {
      if (prefetchedIdle || isSlowNet || isSaveData) return;
      prefetchedIdle = true;
      ['/collections/novedades-1', '/collections/para-mama', '/cart'].forEach(function (url) {
        var link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.as = 'document';
        document.head.appendChild(link);
      });
    }
    setTimeout(function () {
      requestIdleCallback(prefetchIdle, { timeout: 8000 });
    }, 3000);
  }

  // ===========================================================
  // 8. CSS containment hint para sections fuera de viewport
  //    (mejora paint perf en scroll)
  // ===========================================================
  if ('IntersectionObserver' in window && 'content-visibility' in document.documentElement.style) {
    document.documentElement.classList.add('df-cv-supported');
  }

  // ===========================================================
  // 9. Performance metric tracking (LCP, CLS, FID) → dataLayer
  // ===========================================================
  if (window.PerformanceObserver) {
    try {
      // LCP
      var lcpObserver = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        var last = entries[entries.length - 1];
        if (last && window.dataLayer) {
          window.dataLayer.push({
            event: 'df_perf_lcp',
            lcp_value: Math.round(last.renderTime || last.loadTime || 0),
            lcp_element: last.element && last.element.tagName
          });
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {}
    try {
      // CLS
      var clsScore = 0;
      var clsObserver = new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (!entry.hadRecentInput) clsScore += entry.value;
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      window.addEventListener('beforeunload', function () {
        if (window.dataLayer) window.dataLayer.push({ event: 'df_perf_cls', cls_value: clsScore.toFixed(3) });
      });
    } catch (e) {}
  }
})();
